/** Field styles are class strings, so a native `<select>` keeps its behaviour; `TextBox` passes every input attribute through. */
import clsx from 'clsx';
import { RefreshCw } from 'lucide-react';
import { type InputHTMLAttributes, type ReactNode, type Ref, useId } from 'react';
import { KEEP } from '../../lib/usage.ts';
import { Hint } from './shell.tsx';

export function IconButton({
  label,
  onClick,
  disabled = false,
  className,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  className?: string;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className={clsx(
        'rounded-control p-1.5 text-ink-faint transition-colors hover:bg-surface-raised hover:text-ink disabled:opacity-45 touch:p-2',
        className,
      )}
    >
      {children}
    </button>
  );
}

export function RefreshButton({
  label,
  checking,
  onClick,
  className,
}: {
  label: string;
  checking: boolean;
  onClick: () => void;
  className?: string;
}) {
  return (
    <IconButton label={label} onClick={onClick} disabled={checking} className={className}>
      <RefreshCw aria-hidden className={clsx('size-3.5', checking && 'animate-spin')} />
    </IconButton>
  );
}

export function TextBox({
  onChange,
  onSubmit,
  onCancel,
  className,
  ...input
}: {
  value: string;
  onChange: (next: string) => void;
  onSubmit?: (() => void) | undefined;
  onCancel?: () => void;
  className?: string;
  ref?: Ref<HTMLInputElement>;
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'onSubmit' | 'className'>) {
  return (
    <input
      {...input}
      className={className ?? field()}
      onChange={(event) => onChange(event.target.value)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' && onSubmit) {
          event.preventDefault();
          onSubmit();
        }
        if (event.key === 'Escape') onCancel?.();
      }}
    />
  );
}

function Labelled({
  label,
  title,
  prefix,
  suffix,
  children,
}: {
  label: string;
  title?: string | undefined;
  prefix?: string | undefined;
  suffix?: string | undefined;
  children: (id: string) => ReactNode;
}) {
  const id = useId();
  return (
    <label htmlFor={id} className="inline-flex items-center gap-1.5 text-xs text-ink-muted">
      <span title={title}>{label}</span>
      <span className="inline-flex items-baseline gap-1 rounded-control border border-line bg-surface px-2 py-1 touch:py-1.5 focus-within:border-accent">
        {prefix && <span className="text-xs text-ink-faint">{prefix}</span>}
        {children(id)}
        {suffix && <span className="text-xs text-ink-faint">{suffix}</span>}
      </span>
    </label>
  );
}

export function TextInput({
  label,
  title,
  type = 'text',
  ...box
}: {
  label: string;
  title?: string;
  value: string;
  onChange: (next: string) => void;
  onSubmit?: () => void;
  placeholder?: string;
  type?: 'text' | 'date';
}) {
  return (
    <Labelled label={label} title={title}>
      {(id) => (
        <TextBox
          {...box}
          id={id}
          type={type}
          className="w-32 bg-transparent text-sm text-ink outline-none"
        />
      )}
    </Labelled>
  );
}

export function NumberField({
  label,
  title,
  prefix,
  suffix,
  step = '0.05',
  ...box
}: {
  label: string;
  title?: string;
  value: string;
  onChange: (next: string) => void;
  onSubmit?: () => void;
  prefix?: string;
  suffix?: string;
  step?: string;
  placeholder?: string;
}) {
  return (
    <Labelled label={label} title={title} prefix={prefix} suffix={suffix}>
      {(id) => (
        <TextBox
          {...box}
          id={id}
          type="number"
          inputMode="decimal"
          step={step}
          min="0"
          className="tnum w-24 bg-transparent text-sm text-ink outline-none"
        />
      )}
    </Labelled>
  );
}

export function Choices<T extends string | number>({
  options,
  value,
  onSelect,
  label,
}: {
  options: readonly { value: T; label: ReactNode; hint?: string }[];
  value: T | null;
  onSelect: (next: T) => void;
  label: string;
}) {
  return (
    <fieldset aria-label={label} className="flex flex-wrap items-center gap-1.5">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          title={option.hint}
          aria-pressed={option.value === value}
          onClick={() => onSelect(option.value)}
          className={clsx(
            'rounded-control border px-2 py-1 text-xs transition-colors touch:min-h-9 touch:px-3',
            option.value === value
              ? CHOSEN
              : 'border-line text-ink-muted hover:border-ink/40 hover:text-ink',
          )}
        >
          {option.label}
        </button>
      ))}
    </fieldset>
  );
}

export function Checkbox({
  checked,
  onChange,
  disabled = false,
  label,
  children,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  label: ReactNode;
  children?: ReactNode;
}) {
  const id = useId();
  return (
    <div className="flex items-start gap-2">
      <input
        id={id}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
        className="mt-0.5 size-3.5 shrink-0 accent-accent disabled:opacity-45 touch:mt-0 touch:size-4"
      />
      <div className="flex min-w-0 flex-col gap-0.5">
        <label
          htmlFor={id}
          className={clsx('text-xs', disabled ? 'text-ink-faint' : 'cursor-pointer text-ink-muted')}
        >
          {label}
        </label>
        {children}
      </div>
    </div>
  );
}

/** Takes effect on tap; never pair it with a Save button. */
export function Switch({
  on,
  busy = false,
  disabled = false,
  onChange,
  label,
}: {
  on: boolean;
  busy?: boolean;
  disabled?: boolean;
  onChange: (next: boolean) => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      disabled={disabled || busy}
      onClick={() => onChange(!on)}
      className={clsx(
        'relative h-5 w-9 shrink-0 rounded-full transition-colors disabled:opacity-45 touch:h-6 touch:w-11',
        on ? 'bg-accent' : 'bg-surface-raised ring-1 ring-line ring-inset',
      )}
    >
      <span
        aria-hidden
        className={clsx(
          'absolute top-0.5 left-0.5 size-4 rounded-full transition-transform touch:size-5',
          on ? 'translate-x-4 bg-canvas touch:translate-x-5' : 'bg-ink-muted',
        )}
      />
    </button>
  );
}

export const CHOSEN = 'border-ink/70 bg-surface-raised text-ink';

export const field = ({ size = 'sm', mono = false }: { size?: 'sm' | 'lg'; mono?: boolean } = {}) =>
  clsx(
    'w-full rounded-control border border-line bg-canvas text-ink outline-none placeholder:text-ink-faint focus:border-accent',
    size === 'lg' ? 'px-3 py-2.5 text-base' : mono ? 'px-2 py-1 text-xs' : 'px-2.5 py-1.5 text-sm',
    mono && 'font-mono',
  );

export const navLink = (here: boolean) =>
  clsx(
    KEEP,
    'rounded-control px-2.5 py-1 text-xs font-medium whitespace-nowrap transition-colors',
    here ? 'bg-surface-raised text-ink' : 'text-ink-faint hover:bg-surface hover:text-ink',
  );

export const menuItem = ({
  here = false,
  tone = 'quiet',
}: {
  here?: boolean;
  tone?: 'quiet' | 'danger';
} = {}) =>
  clsx(
    KEEP,
    'flex w-full items-center gap-2.5 rounded-control px-2.5 py-2 text-left text-xs font-medium transition-colors touch:py-2.5',
    tone === 'danger'
      ? 'text-down hover:bg-down/10'
      : here
        ? 'bg-surface text-ink'
        : 'text-ink-muted hover:bg-surface hover:text-ink',
  );

/** One control is handed the id its `<label>` names; several are named as a group. */
export function Field({
  label,
  hint,
  children,
}: {
  label: ReactNode;
  hint?: string | undefined;
  children: ReactNode | ((id: string) => ReactNode);
}) {
  const id = useId();
  const said = hint && (
    <Hint as="span" faint>
      {hint}
    </Hint>
  );
  if (typeof children === 'function') {
    return (
      <div className={FIELD}>
        <label htmlFor={id} className="font-medium text-ink">
          {label}
        </label>
        {children(id)}
        {said}
      </div>
    );
  }
  return (
    <fieldset aria-labelledby={id} className={FIELD}>
      <span id={id} className="font-medium text-ink">
        {label}
      </span>
      {children}
      {said}
    </fieldset>
  );
}

const FIELD = 'flex min-w-0 flex-col gap-1 text-xs text-ink-muted';

export function TextField({
  label,
  hint,
  value,
  onChange,
  onSubmit,
  children,
  ...input
}: {
  label: ReactNode;
  hint?: string | undefined;
  value: string | undefined;
  onChange: (value: string) => void;
  onSubmit?: () => void;
  className?: string;
  children?: ReactNode;
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'onSubmit' | 'className'>) {
  return (
    <Field label={label} hint={hint}>
      {(id) => (
        <>
          <TextBox id={id} value={value ?? ''} onChange={onChange} onSubmit={onSubmit} {...input} />
          {children}
        </>
      )}
    </Field>
  );
}

const LINKS = {
  quiet:
    'underline decoration-line underline-offset-2 transition-colors hover:text-ink hover:decoration-current',
  faint:
    'text-xs text-ink-faint underline decoration-line underline-offset-2 transition-colors hover:text-ink hover:decoration-current',
  undo: 'text-xs text-ink-faint underline decoration-line underline-offset-2 transition-colors hover:text-down hover:decoration-current',
  row: 'decoration-line underline-offset-2 transition-colors hover:text-ink hover:underline',
} as const;

export const linkClass = (tone: keyof typeof LINKS = 'quiet', className?: string) =>
  clsx(LINKS[tone], className);
